import { polygonArea } from "../../polygonArea";
import { findZoneLabelRoom } from "../findZoneLabelRoom";
import { getLayoutShapeId } from "../getLayoutShapeId";
import { getPolygonBounds } from "../getPolygonBounds";
import { getWallStrokeWidthPt } from "../getWallStrokeWidthPt";
import { truncateLabel } from "../truncateLabel";
import { NO_ZONE_RGB, NO_ZONE_SHAPE_ID, ZONE_DASH_BY_KIND } from "../constants";
import type { FloorPlanLayoutShape } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelNew/SvgOverlay/FloorPlanLayoutLayer";
import type { MeasuredRoom } from "../findZoneLabelRoom";
import type { BuildZoneShapes } from "./types";

// Everything a zone's shape needs, gathered in one pass over the rooms.
interface ZoneRooms {
  rings: FloorPlanLayoutShape["rings"];
  roomNames: string[];
  totalArea: number;
  measured: MeasuredRoom[];
}

const emptyZoneRooms = (): ZoneRooms => ({
  rings: [],
  roomNames: [],
  totalArea: 0,
  measured: [],
});

// One shape per zone made of all its rooms, plus one grey shape for the rooms without a zone.
export const buildZoneShapes: BuildZoneShapes = ({
  zones,
  rooms,
  zoneColorById,
  metersPerPdfPoint,
}) => {
  const roomsByZoneId = new Map<number | null, ZoneRooms>([
    [null, emptyZoneRooms()],
    ...zones.map((zone): [number, ZoneRooms] => [zone.id, emptyZoneRooms()]),
  ]);

  for (const room of rooms) {
    // A room whose zone is not among the given zones counts as having none.
    const zoneRooms = roomsByZoneId.get(room.zoneId) ?? roomsByZoneId.get(null);
    if (!zoneRooms) continue;
    const area = polygonArea(room.points);
    zoneRooms.rings.push(room.points);
    zoneRooms.roomNames.push(room.name);
    zoneRooms.totalArea += area;
    zoneRooms.measured.push({
      room,
      area,
      bounds: getPolygonBounds({ points: room.points }),
    });
  }

  const touchDistancePt = getWallStrokeWidthPt({ metersPerPdfPoint });
  const shapes: FloorPlanLayoutShape[] = [];

  for (const zone of zones) {
    const zoneRooms = roomsByZoneId.get(zone.id);
    if (!zoneRooms || zoneRooms.measured.length === 0) continue;

    const labelRoom = findZoneLabelRoom({
      rooms: zoneRooms.measured,
      touchDistancePt,
    });
    shapes.push({
      id: getLayoutShapeId({ kind: "zone", id: zone.id }),
      // NOTE: drawn with the non-zero rule, so overlapping rooms wound in opposite directions would leave a hole.
      rings: zoneRooms.rings,
      rgb: zoneColorById.get(zone.id) ?? NO_ZONE_RGB,
      strokeDash: ZONE_DASH_BY_KIND[zone.kind],
      label: { text: truncateLabel(zone.name), points: labelRoom.room.points },
      tooltip: {
        kind: "zone",
        name: zone.name,
        zoneKind: zone.kind,
        roomNames: zoneRooms.roomNames,
        areaM2:
          metersPerPdfPoint > 0
            ? zoneRooms.totalArea * metersPerPdfPoint * metersPerPdfPoint
            : null,
        summary: zone.summary,
      },
    });
  }

  const roomsWithoutZone = roomsByZoneId.get(null);
  if (roomsWithoutZone && roomsWithoutZone.rings.length > 0) {
    shapes.push({
      id: NO_ZONE_SHAPE_ID,
      rings: roomsWithoutZone.rings,
      rgb: NO_ZONE_RGB,
      label: null,
      tooltip: null,
    });
  }

  return shapes;
};
